import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  ChatBubbleLeftRightIcon,
  HeartIcon,
  ShareIcon,
  TrophyIcon,
  UserPlusIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
// import ShareTradeModal from '../components/ShareTradeModal'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import toast from 'react-hot-toast'

const Social = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('my-posts')
  const [showShareModal, setShowShareModal] = useState(false)
  const [userPosts, setUserPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [userTrades, setUserTrades] = useState([])
  const [userStats, setUserStats] = useState({
    totalTrades: 0,
    totalPnL: 0,
    winRate: 0,
    bestTrade: null,
    worstTrade: null
  })
  
  // Load user's posts and trades
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        
        // Load saved posts from localStorage
        const savedPosts = localStorage.getItem('socialPosts')
        if (savedPosts) {
          setUserPosts(JSON.parse(savedPosts))
        }
        
        // Fetch user's trades for stats and sharing
        if (user) {
          const tradesResponse = await api.trades.getAll({ limit: 100 })
          const trades = tradesResponse.data.trades || []
          setUserTrades(trades)
          
          // Calculate user stats
          const totalPnL = trades.reduce((sum, trade) => sum + (trade.pnl || 0), 0)
          const winningTrades = trades.filter(trade => (trade.pnl || 0) > 0)
          const winRate = trades.length > 0 ? (winningTrades.length / trades.length * 100) : 0
          const bestTrade = trades.reduce((best, trade) => 
            (trade.pnl || 0) > (best?.pnl || 0) ? trade : best, null)
          const worstTrade = trades.reduce((worst, trade) => 
            (trade.pnl || 0) < (worst?.pnl || 0) ? trade : worst, null)
          
          setUserStats({
            totalTrades: trades.length,
            totalPnL,
            winRate,
            bestTrade,
            worstTrade
          })
        }
        
      } catch (error) {
        console.error('Error loading social data:', error)
        toast.error('Failed to load your trading data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user])

  // Save posts to localStorage whenever posts change
  useEffect(() => {
    if (userPosts.length > 0) {
      localStorage.setItem('socialPosts', JSON.stringify(userPosts))
    }
  }, [userPosts])

  const handleShareTrade = (tradeData) => {
    // Create a new post from the shared trade data
    const selectedTrade = userTrades.find(t => t.id === tradeData.tradeId)
    const newPost = {
      id: `user-${Date.now()}`,
      user: {
        name: user?.firstName || 'You',
        avatar: user?.firstName ? user.firstName.charAt(0).toUpperCase() + (user.lastName?.charAt(0).toUpperCase() || '') : 'YU',
        verified: false
      },
      content: tradeData.content || '', // Additional thoughts from share modal
      originalNotes: selectedTrade?.notes || '', // Original trade notes
      timestamp: new Date().toISOString(),
      likes: 0,
      comments: 0,
      shares: 0,
      tags: tradeData.tags ? tradeData.tags.split(' ').filter(tag => tag.trim()) : [],
      trade: {
        symbol: tradeData.symbol,
        side: tradeData.side === 'buy' ? 'long' : 'short',
        pnl: `${parseFloat(tradeData.pnl) >= 0 ? '+' : ''}$${Math.abs(parseFloat(tradeData.pnl)).toFixed(2)}`,
        percentage: `${parseFloat(tradeData.percentage) >= 0 ? '+' : ''}${tradeData.percentage}%`,
        entryPrice: selectedTrade?.entry_price,
        exitPrice: selectedTrade?.exit_price,
        quantity: selectedTrade?.quantity,
        entryTime: selectedTrade?.entry_time,
        exitTime: selectedTrade?.exit_time
      }
    }
    
    // Add the new post to the beginning of the posts array
    setUserPosts([newPost, ...userPosts])
    toast.success('Journaled trade shared successfully!')
  }

  const handleLike = (postId) => {
    setUserPosts(userPosts.map(post => 
      post.id === postId 
        ? { ...post, likes: post.likes + 1 }
        : post
    ))
  }

  const handleDeletePost = (postId) => {
    setUserPosts(userPosts.filter(post => post.id !== postId))
    toast.success('Post deleted')
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return date.toLocaleDateString()
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  if (loading) {
    return (
      <div className="relative min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-full">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">
                  Social Trading 👥
                </h1>
                <p className="text-gray-300 mt-1">
                  Share your journaled trades and insights with the trading community
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowShareModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Share Journaled Trade
              </motion.button>
            </motion.div>

            {/* Tabs */}
            <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
              <div className="flex space-x-2">
                {[
                  { id: 'my-posts', label: 'My Posts', icon: ChatBubbleLeftRightIcon },
                  { id: 'stats', label: 'My Stats', icon: TrophyIcon },
                  { id: 'achievements', label: 'Achievements', icon: UserPlusIcon }
                ].map((tab) => (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {activeTab === 'my-posts' && (
                  <>
                    {userPosts.length === 0 ? (
                      <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                        <div className="text-center py-12">
                          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-white mb-2">No Shared Trades Yet</h3>
                          <p className="text-gray-300 mb-6">
                            Share your journaled trades with the community. Only trades with notes and analysis can be shared socially.
                          </p>
                          <button 
                            onClick={() => setShowShareModal(true)}
                            className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                          >
                            Share a Journaled Trade
                          </button>
                        </div>
                      </motion.div>
                    ) : (
                      userPosts.map((post, index) => (
                        <motion.div
                          key={post.id}
                          variants={itemVariants}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl"
                        >
                          {/* User Info */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">{post.user.avatar}</span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h3 className="text-white font-semibold">{post.user.name}</h3>
                                  <span className="px-2 py-1 text-xs bg-indigo-500/20 text-indigo-400 rounded-full border border-indigo-500/30">
                                    You
                                  </span>
                                </div>
                                <p className="text-xs text-gray-400">{formatDate(post.timestamp)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="text-gray-400 hover:text-red-400 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>

                          {/* Content */}
                          {post.originalNotes && (
                            <div className="mb-4">
                              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 mb-3">
                                <h4 className="text-sm font-medium text-indigo-400 mb-2">📝 Original Trade Notes</h4>
                                <p className="text-gray-200 text-sm italic">"{post.originalNotes}"</p>
                              </div>
                            </div>
                          )}
                          
                          {post.content && (
                            <div className="mb-4">
                              <p className="text-gray-200">{post.content}</p>
                            </div>
                          )}

                          {/* Tags */}
                          {post.tags && post.tags.length > 0 && (
                            <div className="mb-4">
                              <div className="flex flex-wrap gap-2">
                                {post.tags.map((tag, index) => (
                                  <span 
                                    key={index}
                                    className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded-full border border-purple-500/30"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Trade Info */}
                          {post.trade && (
                            <div className="bg-black/20 rounded-xl p-4 mb-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <span className="text-white font-semibold">{post.trade.symbol}</span>
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                                    post.trade.side === 'long' 
                                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                                  }`}>
                                    {post.trade.side.toUpperCase()}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className={`font-bold ${
                                    post.trade.pnl.startsWith('+') ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {post.trade.pnl}
                                  </div>
                                  <div className="text-xs text-gray-400">{post.trade.percentage}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center space-x-6 pt-4 border-t border-white/10">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleLike(post.id)}
                              className="flex items-center space-x-2 text-gray-400 hover:text-red-400 transition-colors"
                            >
                              <HeartIcon className="w-5 h-5" />
                              <span className="text-sm">{post.likes}</span>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="flex items-center space-x-2 text-gray-400 hover:text-blue-400 transition-colors"
                            >
                              <ChatBubbleLeftRightIcon className="w-5 h-5" />
                              <span className="text-sm">{post.comments}</span>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="flex items-center space-x-2 text-gray-400 hover:text-green-400 transition-colors"
                            >
                              <ShareIcon className="w-5 h-5" />
                              <span className="text-sm">{post.shares}</span>
                            </motion.button>
                          </div>
                        </motion.div>
                      ))
                    )}
                  </>
                )}

                {activeTab === 'stats' && (
                  <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                    <h3 className="text-xl font-bold text-white mb-6">Your Trading Performance</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-black/20 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-white mb-4">Overall Stats</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Total Trades</span>
                            <span className="text-white font-semibold">{userStats.totalTrades}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Total P&L</span>
                            <span className={`font-semibold ${userStats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(userStats.totalPnL)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Win Rate</span>
                            <span className="text-indigo-400 font-semibold">{userStats.winRate.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-black/20 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-white mb-4">Best & Worst</h4>
                        <div className="space-y-3">
                          {userStats.bestTrade && (
                            <div>
                              <span className="text-gray-300 text-sm">Best Trade</span>
                              <div className="flex justify-between items-center">
                                <span className="text-white font-medium">{userStats.bestTrade.symbol}</span>
                                <span className="text-green-400 font-semibold">
                                  {formatCurrency(userStats.bestTrade.pnl)}
                                </span>
                              </div>
                            </div>
                          )}
                          {userStats.worstTrade && (
                            <div>
                              <span className="text-gray-300 text-sm">Worst Trade</span>
                              <div className="flex justify-between items-center">
                                <span className="text-white font-medium">{userStats.worstTrade.symbol}</span>
                                <span className="text-red-400 font-semibold">
                                  {formatCurrency(userStats.worstTrade.pnl)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'achievements' && (
                  <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                    <div className="text-center py-12">
                      <TrophyIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">Achievements Coming Soon</h3>
                      <p className="text-gray-300">
                        Track your trading milestones and unlock achievements based on your performance.
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">Quick Stats</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Posts Shared</span>
                      <span className="text-white font-semibold">{userPosts.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total Trades</span>
                      <span className="text-white font-semibold">{userStats.totalTrades}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Win Rate</span>
                      <span className="text-indigo-400 font-semibold">{userStats.winRate.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Total P&L</span>
                      <span className={`font-semibold ${userStats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(userStats.totalPnL)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Recent Trades */}
                <motion.div variants={itemVariants} className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                  <h3 className="text-xl font-bold text-white mb-6">Recent Trades</h3>
                  <div className="space-y-3">
                    {userTrades.slice(0, 5).map((trade, index) => (
                      <div key={trade.id || index} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                        <div>
                          <span className="text-white font-medium">{trade.symbol}</span>
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                            trade.side === 'buy' 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {trade.side?.toUpperCase()}
                          </span>
                        </div>
                        <span className={`font-semibold ${
                          (trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {formatCurrency(trade.pnl || 0)}
                        </span>
                      </div>
                    ))}
                    {userTrades.length === 0 && (
                      <p className="text-gray-400 text-sm text-center py-4">
                        No trades yet. Start trading to see your activity here.
                      </p>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Share Trade Modal */}
      {/* <ShareTradeModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onSubmit={handleShareTrade}
      /> */}
    </div>
  )
}

export default Social